import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Container, Input, Row, Badge } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { getAllServices } from '../../../helpers/fakebackend_helper';

// Visual grouping. Any service_id not listed here falls into "Other".
const CATEGORIES = [
  {
    key: "image",
    label: "Image Generation",
    icon: "ri-image-2-line",
    color: "#7c3aed",
    ids: [
      "TEXT_TO_IMAGE", "IMAGE_TO_IMAGE", "ENHANCE_IMAGE",
      "REMOVE_BACKGROUND", "REMOVE_OBJECT", "SWAP_OUTFIT",
      "AI_AVATAR", "SWAP_FACE_IMAGE",
    ],
  },
  {
    key: "video",
    label: "Video Generation",
    icon: "ri-movie-2-line",
    color: "#2563eb",
    ids: [
      "IMAGE_TO_VIDEO", "TEXT_TO_VIDEO", "TRANSITION_VIDEO",
      "LIPSYNC", "LIVEPORTRAIT", "AUDIO_TO_VIDEO",
      "SWAP_FACE_VIDEO", "PHOTO_DANCE",
    ],
  },
  {
    key: "audio",
    label: "Audio & Music",
    icon: "ri-music-2-line",
    color: "#db2777",
    ids: [
      "SONG_GENERATE_WITH_PROMPT", "SONG_GENERATE_WITH_LYRICS",
      "SONG_PROMPT_SUGGEST", "AUDIO_SEPERATE",
      "COVER_MODEL_UPLOAD", "COVER_MODEL_TRAIN", "COVER_GENERATE",
      "YOUTUBE_DOWNLOAD",
    ],
  },
  {
    key: "identification",
    label: "Identification",
    icon: "ri-leaf-line",
    color: "#059669",
    ids: [
      "IDENTIFY_PLANT", "IDENTIFY_PLANT_DETAIL",
      "IDENTIFY_PLANT_DETAIL_BY_NAME", "IDENTIFY_PLANT_DISEASE",
      "IDENTIFY_INSECT", "IDENTIFY_BIRD",
      "IDENTIFY_MUSHROOM", "IDENTIFY_STONE", "IDENTIFY_COIN",
    ],
  },
  {
    key: "utility",
    label: "Utilities",
    icon: "ri-tools-line",
    color: "#d97706",
    ids: ["CHECK_NSFW", "CALL_VLLM"],
  },
];

const ICON_BY_ID = {
  TEXT_TO_IMAGE: "ri-quill-pen-line",
  IMAGE_TO_IMAGE: "ri-image-edit-line",
  ENHANCE_IMAGE: "ri-sparkling-line",
  REMOVE_BACKGROUND: "ri-eraser-line",
  REMOVE_OBJECT: "ri-scissors-cut-line",
  SWAP_OUTFIT: "ri-shirt-line",
  AI_AVATAR: "ri-user-smile-line",
  SWAP_FACE_IMAGE: "ri-emotion-line",
  IMAGE_TO_VIDEO: "ri-movie-line",
  TEXT_TO_VIDEO: "ri-film-line",
  TRANSITION_VIDEO: "ri-film-line",
  LIPSYNC: "ri-mic-2-line",
  LIVEPORTRAIT: "ri-user-voice-line",
  AUDIO_TO_VIDEO: "ri-video-line",
  SWAP_FACE_VIDEO: "ri-video-add-line",
  PHOTO_DANCE: "ri-magic-line",
  SONG_GENERATE_WITH_PROMPT: "ri-music-2-line",
  SONG_GENERATE_WITH_LYRICS: "ri-file-music-line",
  SONG_PROMPT_SUGGEST: "ri-lightbulb-line",
  AUDIO_SEPERATE: "ri-equalizer-line",
  COVER_MODEL_UPLOAD: "ri-upload-cloud-line",
  COVER_MODEL_TRAIN: "ri-cpu-line",
  COVER_GENERATE: "ri-album-line",
  YOUTUBE_DOWNLOAD: "ri-youtube-line",
  IDENTIFY_PLANT: "ri-plant-line",
  IDENTIFY_PLANT_DETAIL: "ri-leaf-line",
  IDENTIFY_PLANT_DETAIL_BY_NAME: "ri-search-line",
  IDENTIFY_PLANT_DISEASE: "ri-virus-line",
  IDENTIFY_INSECT: "ri-bug-line",
  IDENTIFY_BIRD: "ri-bird-line",
  IDENTIFY_MUSHROOM: "ri-leaf-fill",
  IDENTIFY_STONE: "ri-stack-line",
  IDENTIFY_COIN: "ri-coin-line",
  CHECK_NSFW: "ri-shield-check-line",
  CALL_VLLM: "ri-robot-line",
};

const formatName = (svc) => {
  if (svc.service_name && svc.service_name.trim()) return svc.service_name;
  return svc.service_id
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
};

const Pricing = () => {
  document.title = "API Pricing | ApiHub.today";
  const [services, setServices] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getAllServices();
        if (!cancelled && res && res.success) {
          setServices(res.data || []);
        }
      } catch (e) {
        console.log("load services error", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const idToCategory = {};
    CATEGORIES.forEach(cat => cat.ids.forEach(id => { idToCategory[id] = cat.key; }));

    const buckets = {};
    CATEGORIES.forEach(cat => { buckets[cat.key] = []; });
    buckets.other = [];

    services.forEach(svc => {
      const name = formatName(svc).toLowerCase();
      if (q && !name.includes(q) && !svc.service_id.toLowerCase().includes(q)) return;
      const catKey = idToCategory[svc.service_id] || "other";
      (buckets[catKey] = buckets[catKey] || []).push(svc);
    });

    Object.keys(buckets).forEach(k => {
      buckets[k].sort((a, b) => (a.credit || 0) - (b.credit || 0));
    });
    return buckets;
  }, [services, query]);

  const totalServices = services.length;
  const visibleCount = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="API Pricing" pageTitle="Pages" />

          {/* Hero */}
          <div className="ah-hero-card mb-4">
            <div className="ah-hero-inner">
              <Row className="align-items-center">
                <Col lg={8}>
                  <div className="ah-hero-eyebrow">Pricing</div>
                  <h1 className="ah-hero-title">Pay-per-call API Pricing</h1>
                  <p className="ah-hero-sub">
                    Transparent credit cost for every API. No hidden fees. Buy credits or subscribe to a plan for better rates.
                  </p>
                  <div className="d-flex gap-2 flex-wrap mt-3">
                    <Link to="/plan" className="btn btn-primary fw-medium">
                      <i className="ri-vip-crown-2-line me-1"></i> Compare Subscription Plans
                    </Link>
                    <Link to="/apps-api-playground" className="btn btn-outline-secondary fw-medium">
                      <i className="ri-play-circle-line me-1"></i> Try in Playground
                    </Link>
                  </div>
                </Col>
                <Col lg={4} className="text-lg-end mt-4 mt-lg-0">
                  <div className="ah-hero-stat">
                    <span className="ah-hero-stat-label">APIs available</span>
                    <span className="ah-hero-stat-num">{totalServices}</span>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {/* Search */}
          <Card className="mb-4">
            <CardBody className="d-flex align-items-center gap-3 flex-wrap">
              <div className="search-box flex-grow-1" style={{ maxWidth: 480 }}>
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Search APIs..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
              <div className="text-muted fs-13">
                {loading ? "Loading..." : `${visibleCount} of ${totalServices} APIs`}
              </div>
            </CardBody>
          </Card>

          {/* Categories */}
          {CATEGORIES.map(cat => {
            const items = grouped[cat.key] || [];
            if (items.length === 0) return null;
            return (
              <Card key={cat.key} className="mb-4 ah-cat-card">
                <CardHeader className="d-flex align-items-center gap-3">
                  <div className="ah-cat-icon">
                    <i className={cat.icon}></i>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="mb-0">{cat.label}</h5>
                    <small className="text-muted">{items.length} {items.length === 1 ? "API" : "APIs"}</small>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    {items.map(svc => (
                      <Col key={svc.service_id} xl={4} lg={6} md={6}>
                        <ServiceCard service={svc} categoryColor={cat.color} />
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            );
          })}

          {/* Other / unmapped */}
          {grouped.other && grouped.other.length > 0 && (
            <Card className="mb-4 ah-cat-card">
              <CardHeader className="d-flex align-items-center gap-3">
                <div className="ah-cat-icon">
                  <i className="ri-apps-2-line"></i>
                </div>
                <div className="flex-grow-1">
                  <h5 className="mb-0">Other</h5>
                  <small className="text-muted">{grouped.other.length} {grouped.other.length === 1 ? "API" : "APIs"}</small>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  {grouped.other.map(svc => (
                    <Col key={svc.service_id} xl={4} lg={6} md={6}>
                      <ServiceCard service={svc} categoryColor="#64748b" />
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          )}

          {!loading && totalServices === 0 && (
            <Card>
              <CardBody className="text-center py-5">
                <i className="ri-error-warning-line display-4 text-muted"></i>
                <p className="text-muted mt-3 mb-0">No APIs available right now.</p>
              </CardBody>
            </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

const ServiceCard = ({ service }) => {
  const icon = ICON_BY_ID[service.service_id] || "ri-circle-line";
  return (
    <div className="ah-svc-card">
      <div className="d-flex align-items-start gap-3 mb-2">
        <div className="ah-svc-icon">
          <i className={icon}></i>
        </div>
        <div className="flex-grow-1">
          <h6 className="mb-1 fw-semibold">{formatName(service)}</h6>
          <code className="ah-svc-id">{service.service_id}</code>
        </div>
      </div>
      <div className="ah-svc-foot">
        <small>Cost per call</small>
        <span className="ah-credits-pill">
          {service.credit} {service.credit === 1 ? "credit" : "credits"}
        </span>
      </div>
    </div>
  );
};

export default Pricing;
